import * as z from "zod";

const timePeriodSchema = z.enum([
  "day",
  "week",
  "month",
  "last-month",
  "quarter",
  "all",
  "custom",
]);

const customRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const selfServeOnboardingStatsSchema = z.object({
  totalCount: z.number().int().min(0),
  completedCount: z.number().int().min(0),
  helpRequestedCount: z.number().int().min(0),
  totalRevenue: z.number().min(0),
  weightedRevenueOnboarded: z.number().min(0),
  averageCompletionTimeMinutes: z.number().min(0),
  averageCompletionPercentage: z.number().min(0).max(100),
  // Previous period comparisons (null for custom time ranges)
  totalCountChange: z.number().nullable().optional(),
  completedCountChange: z.number().nullable().optional(),
  helpRequestedCountChange: z.number().nullable().optional(),
  totalRevenueChange: z.number().nullable().optional(),
  weightedRevenueOnboardedChange: z.number().nullable().optional(),
  averageCompletionTimeMinutesChange: z.number().nullable().optional(),
  averageCompletionPercentageChange: z.number().nullable().optional(),
  // Pre-computed rate deltas in percentage points. Server-side
  // computation centralizes the division-by-zero + null-previous-period
  // guards in analytics/changes.ts. Optional because legacy handlers
  // haven't been migrated to populate them.
  completionRateChange: z.number().nullable().optional(),
  containmentRateChange: z.number().nullable().optional(),
});

const statusFilterSchema = z.enum([
  "Completed",
  "Reopened",
  "Awaiting approval",
  "In progress",
  "Partial Completion",
  "Help Requested",
]);

const numericFilterSchema = z.object({
  operator: z.enum(["lt", "gt"]),
  value: z.number().min(0),
});

const dateRangeFilterSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const getSelfServeOnboardingStats = {
  name: "getSelfServeOnboardingStats" as const,
  input: z
    .object({
      timePeriod: timePeriodSchema,
      customRange: customRangeSchema.optional(),
      // Advanced filters (matching merchants page)
      statuses: z.array(statusFilterSchema).optional(),
      configurationPushedFilter: z.enum(["yes", "no"]).optional(),
      startDateRange: dateRangeFilterSchema.optional(),
      durationFilter: numericFilterSchema.optional(),
      dealSizeFilter: numericFilterSchema.optional(),
      filterLowIntent: z.boolean().optional(),
      typeFilter: z.enum(["all", "Self-Serve", "Custom"]).optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timePeriod === "custom" && !value.customRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is custom",
          path: ["customRange"],
        });
      }
    }),
  output: selfServeOnboardingStatsSchema,
};

export type SelfServeOnboardingStats = z.infer<
  typeof selfServeOnboardingStatsSchema
>;
export type GetSelfServeOnboardingStatsInput = z.infer<
  typeof getSelfServeOnboardingStats.input
>;
export type GetSelfServeOnboardingStatsOutput = z.infer<
  typeof getSelfServeOnboardingStats.output
>;
